// Analytics Tracker Component - Enhanced with funnel tracking
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_DURATION = 1800; // Cap at 30 minutes

const getVisitorId = () => {
  let id = localStorage.getItem('visitor_id');
  if (!id) {
    id = 'v_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('visitor_id', id);
  }
  return id;
};

const getSessionId = () => {
  let id = sessionStorage.getItem('session_id');
  if (!id) {
    id = 's_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('session_id', id);
  }
  return id;
};

const sendEvent = async (data) => {
  try {
    await axios.post(`${API}/analytics/track`, {
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      page_url: window.location.href,
      page_title: document.title,
      user_agent: navigator.userAgent,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      referrer: document.referrer || null,
      utm_source: new URLSearchParams(window.location.search).get('utm_source'),
      utm_medium: new URLSearchParams(window.location.search).get('utm_medium'),
      utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign'),
      ...data,
    }, { headers: { 'Content-Type': 'application/json' } });
  } catch (e) {
    // silent
  }
};

const sendBeacon = (data) => {
  try {
    fetch(`${API}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        page_url: window.location.href,
        page_title: document.title,
        ...data,
      }),
      keepalive: true,
    });
  } catch (e) {
    // silent
  }
};

// Public API for tracking clicks from any component
export const trackClick = (action, label, extra = {}) => {
  sendEvent({
    event_type: 'click',
    click_action: action,
    click_label: label,
    ...extra,
  });
};

const AnalyticsTracker = () => {
  const location = useLocation();
  const startTimeRef = useRef(Date.now());
  const pageInfoRef = useRef({ url: window.location.href, title: document.title });

  useEffect(() => {
    const pageUrl = window.location.href;
    const pageTitle = document.title;
    const pageStart = Date.now();

    startTimeRef.current = pageStart;
    pageInfoRef.current = { url: pageUrl, title: pageTitle };

    sendEvent({ event_type: 'page_view' });

    // Auto-detect funnel events from URL
    const path = window.location.pathname;
    if (path.match(/\/programs\/[a-z0-9-]+/i)) {
      sendEvent({ event_type: 'course_view', page_url: pageUrl });
    }
    if (path === '/register-school') {
      sendEvent({ event_type: 'signup_start', page_url: pageUrl });
    }

    return () => {
      const dur = Math.min(Math.round((Date.now() - pageStart) / 1000), MAX_DURATION);
      if (dur > 0) {
        sendBeacon({ event_type: 'page_leave', duration: dur, page_url: pageUrl, page_title: pageTitle });
      }
    };
  }, [location]);

  // Tab close / refresh
  useEffect(() => {
    const handleUnload = () => {
      const dur = Math.min(Math.round((Date.now() - startTimeRef.current) / 1000), MAX_DURATION);
      if (dur > 0) {
        sendBeacon({ event_type: 'page_leave', duration: dur, ...pageInfoRef.current });
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Track clicks on key CTAs (delegate from document)
  useEffect(() => {
    const handler = (e) => {
      const el = e.target.closest('[data-track]');
      if (el) {
        const action = el.getAttribute('data-track');
        const label = el.textContent?.trim().substring(0, 50) || '';
        trackClick(action, label);
      }
      // Auto-track buttons with specific text
      const btn = e.target.closest('button, a');
      if (btn) {
        const text = (btn.textContent || '').toLowerCase();
        if (text.includes('book now') || text.includes('book this') || text.includes('予約')) {
          trackClick('book_click', btn.textContent.trim().substring(0, 50));
        } else if (text.includes('contact') || text.includes('enquir') || text.includes('問い合わせ')) {
          trackClick('enquiry_click', btn.textContent.trim().substring(0, 50));
        } else if (text.includes('sign up') || text.includes('register') || text.includes('登録')) {
          trackClick('signup_click', btn.textContent.trim().substring(0, 50));
        } else if (text.includes('view course') || text.includes('view program') || text.includes('learn more')) {
          trackClick('course_click', btn.textContent.trim().substring(0, 50));
        }
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, []);

  return null;
};

export default AnalyticsTracker;
